import {
  build,
  findItem,
  formatTime,
  itemEnd,
  type CaptionStyle,
  type TimelineItem,
} from '@easyedit/timeline'
import type { MediaAsset } from '../../lib/api/types'
import { useEditorStore } from '../../stores/editorStore'

const SPEEDS = [0.25, 0.5, 0.75, 1, 1.1, 1.25, 1.5, 2]

/**
 * Propriedades do item selecionado.
 *
 * Toda alteração aqui vira um comando — as mesmas primitivas que a IA usa. Não existe um
 * caminho "de UI" e outro "de IA": é o mesmo sistema.
 */
export function Inspector({ assets }: { assets: Map<string, MediaAsset> }) {
  const sequence = useEditorStore((s) => s.sequence)
  const selected = useEditorStore((s) => s.selectedItemIds)
  const dispatch = useEditorStore((s) => s.dispatch)
  const playhead = useEditorStore((s) => s.playhead)

  const found = sequence && selected.length === 1 ? findItem(sequence, selected[0]!) : undefined

  if (!sequence) return <aside className="w-[280px] shrink-0 border-l border-ink-750 bg-ink-850" />

  if (!found) {
    return (
      <aside className="w-[280px] shrink-0 border-l border-ink-750 bg-ink-850 p-3">
        <h2 className="text-xs font-semibold mb-2">Propriedades</h2>
        <p className="text-2xs text-ink-500 leading-relaxed">
          {selected.length > 1
            ? `${selected.length} itens selecionados. Selecione um só para editar propriedades.`
            : 'Selecione um clip na timeline.'}
        </p>
      </aside>
    )
  }

  const { item, track } = found
  const ctx = { sequenceId: sequence.id }
  const asset = item.source ? assets.get(item.source.assetId) : undefined

  /**
   * Um ajuste de transformação.
   *
   * A chave de coalescência é a PROPRIEDADE que está sendo mexida: arrastar a opacidade
   * e depois a escala são dois passos de undo, mas arrastar a opacidade de 100% a 40%
   * é um só — mesmo que o slider dispare quarenta comandos no caminho.
   */
  const set = (patch: Parameters<typeof build.setTransform>[2]) =>
    void dispatch(build.setTransform(ctx, item.id, patch), {
      coalesceKey: `transform:${item.id}:${Object.keys(patch).join(',')}`,
    })

  return (
    <aside className="w-[280px] shrink-0 border-l border-ink-750 bg-ink-850 overflow-y-auto">
      <div className="p-3 flex flex-col gap-4">
        <div>
          <h2 className="text-xs font-semibold">Propriedades</h2>
          <p className="mt-0.5 text-2xs text-ink-500 truncate">
            {item.label ?? asset?.filename ?? item.kind} · {track.name}
          </p>
        </div>

        <Group title="Tempo">
          <Readout label="Início" value={formatTime(item.start)} />
          <Readout label="Fim" value={formatTime(itemEnd(item))} />
          <Readout label="Duração" value={`${item.duration.toFixed(2)}s`} />
          {item.source && (
            <Readout
              label="Origem"
              value={`${item.source.in.toFixed(2)} → ${item.source.out.toFixed(2)}`}
            />
          )}
          <div className="flex gap-1.5 pt-1">
            <button
              className="btn-outline flex-1 justify-center"
              onClick={() => void dispatch(build.split(ctx, item.id, playhead))}
              disabled={playhead <= item.start || playhead >= itemEnd(item)}
              title="Dividir no playhead (S)"
            >
              Dividir
            </button>
            <button
              className="btn-outline flex-1 justify-center"
              onClick={() => void dispatch(build.duplicate(ctx, [item.id]))}
            >
              Duplicar
            </button>
          </div>
        </Group>

        {(item.kind === 'video' || item.kind === 'image' || item.kind === 'shape' || item.kind === 'text') && (
          <Group title="Transformação">
            {/* Texto entra aqui também. Enquanto não entrava, um título criado pela IA
                não podia ser movido nem girado por quem estava refinando — que é
                exatamente o que a segunda metade da tese do produto promete. */}
            <Slider
              id="insp-scale" label="Escala" value={item.transform.scale}
              min={0.1} max={4} step={0.01} format={(v) => `${Math.round(v * 100)}%`}
              onChange={(scale) => set({ scale })}
            />
            <Slider
              id="insp-x" label="Posição X" value={item.transform.x}
              min={-sequence.width / 2} max={sequence.width / 2} step={1}
              format={(v) => `${Math.round(v)}px`} onChange={(x) => set({ x })}
            />
            <Slider
              id="insp-y" label="Posição Y" value={item.transform.y}
              min={-sequence.height / 2} max={sequence.height / 2} step={1}
              format={(v) => `${Math.round(v)}px`} onChange={(y) => set({ y })}
            />
            <Slider
              id="insp-rot" label="Rotação" value={item.transform.rotation}
              min={-180} max={180} step={1} format={(v) => `${Math.round(v)}°`}
              onChange={(rotation) => set({ rotation })}
            />
            <Slider
              id="insp-op" label="Opacidade" value={item.transform.opacity}
              min={0} max={1} step={0.01} format={(v) => `${Math.round(v * 100)}%`}
              onChange={(opacity) => set({ opacity })}
            />
            {item.keyframes.length > 0 && (
              <p className="text-2xs text-teal leading-relaxed">
                ◆ {item.keyframes.length} keyframes. Os valores acima são os estáticos; a
                animação tem prioridade durante a reprodução.
              </p>
            )}
          </Group>
        )}

        {(item.kind === 'video' || item.kind === 'image') && (
          <Group title="Recorte">
            {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
              <Slider
                key={side}
                id={`insp-crop-${side}`}
                label={CROP_LABELS[side] ?? side}
                value={item.crop[side]}
                min={0} max={0.9} step={0.005}
                format={(v) => `${Math.round(v * 100)}%`}
                onChange={(value) =>
                  void dispatch(build.setCrop(ctx, item.id, { [side]: value }), {
                    coalesceKey: `crop:${item.id}:${side}`,
                  })
                }
              />
            ))}
          </Group>
        )}

        {item.source && (
          <Group title="Velocidade">
            <div className="grid grid-cols-4 gap-1">
              {SPEEDS.map((speed) => (
                <button
                  key={speed}
                  className={`rounded px-1 py-1 text-2xs tnum transition-colors ${
                    Math.abs(item.speed - speed) < 0.001
                      ? 'bg-accent text-ink-950 font-semibold'
                      : 'bg-ink-800 text-ink-300 hover:bg-ink-750'
                  }`}
                  onClick={() => void dispatch(build.setSpeed(ctx, item.id, speed, { preserve: 'source' }))}
                >
                  {speed}×
                </button>
              ))}
            </div>
            <p className="text-2xs text-ink-500 leading-relaxed">
              A duração na timeline é recalculada mantendo o material de origem.
            </p>
          </Group>
        )}

        {(item.kind === 'image' || item.kind === 'text' || item.kind === 'shape') && (
          <Group title="Entrada e saída">
            <Slider
              id="insp-vfin" label="Fade in" value={item.fadeIn}
              min={0} max={Math.min(5, item.duration / 2)} step={0.05}
              format={(v) => `${v.toFixed(2)}s`}
              onChange={(fadeIn) =>
                void dispatch(build.setVolume(ctx, item.id, { fadeIn }), {
                  coalesceKey: `volume:${item.id}:fadeIn`,
                })
              }
            />
            <Slider
              id="insp-vfout" label="Fade out" value={item.fadeOut}
              min={0} max={Math.min(5, item.duration / 2)} step={0.05}
              format={(v) => `${v.toFixed(2)}s`}
              onChange={(fadeOut) =>
                void dispatch(build.setVolume(ctx, item.id, { fadeOut }), {
                  coalesceKey: `volume:${item.id}:fadeOut`,
                })
              }
            />
            <p className="text-2xs text-ink-500 leading-relaxed">
              O fade vale para a imagem, não só para o som: é o que dissolve o elemento
              em vez de fazê-lo aparecer de um frame para o outro.
            </p>
          </Group>
        )}

        {item.kind === 'text' && item.text && (
          <TextGroup
            item={item}
            onPatch={(patch) =>
              void dispatch(build.updateText(ctx, item.id, patch), {
                coalesceKey: `text:${item.id}:${Object.keys(patch).join(',')}`,
              })
            }
          />
        )}

        {item.kind === 'caption' && item.caption && (
          <CaptionGroup
            item={item}
            onPatch={(patch) =>
              void dispatch(build.updateCaptionStyle(ctx, [item.id], patch), {
                coalesceKey: `caption:${item.id}:${Object.keys(patch).join(',')}`,
              })
            }
            onPatchAll={(patch) => {
              // Legenda quase nunca é um bloco só: mudar o estilo de UM e deixar os
              // outros diferentes não é o que ninguém quer.
              const ids = sequence.tracks
                .filter((t) => t.kind === 'caption')
                .flatMap((t) => t.items)
                .filter((i) => i.caption)
                .map((i) => i.id)
              void dispatch(build.updateCaptionStyle(ctx, ids, patch), {
                coalesceKey: `caption:all:${Object.keys(patch).join(',')}`,
              })
            }}
          />
        )}

        {(item.kind === 'audio' || item.kind === 'video') && (
          <Group title="Áudio">
            <Slider
              id="insp-vol" label="Volume" value={item.volume}
              min={0} max={2} step={0.01} format={(v) => `${Math.round(v * 100)}%`}
              onChange={(volume) =>
                void dispatch(build.setVolume(ctx, item.id, { volume }), {
                  coalesceKey: `volume:${item.id}:volume`,
                })
              }
            />
            <Slider
              id="insp-fin" label="Fade in" value={item.fadeIn}
              min={0} max={Math.min(5, item.duration)} step={0.05}
              format={(v) => `${v.toFixed(2)}s`}
              onChange={(fadeIn) =>
                void dispatch(build.setVolume(ctx, item.id, { fadeIn }), {
                  coalesceKey: `volume:${item.id}:fadeIn`,
                })
              }
            />
            <Slider
              id="insp-fout" label="Fade out" value={item.fadeOut}
              min={0} max={Math.min(5, item.duration)} step={0.05}
              format={(v) => `${v.toFixed(2)}s`}
              onChange={(fadeOut) =>
                void dispatch(build.setVolume(ctx, item.id, { fadeOut }), {
                  coalesceKey: `volume:${item.id}:fadeOut`,
                })
              }
            />
            <label className="flex items-center gap-2 text-2xs text-ink-300 cursor-pointer">
              <input
                id="insp-mute"
                type="checkbox"
                checked={item.muted}
                onChange={(e) => void dispatch(build.setVolume(ctx, item.id, { muted: e.target.checked }))}
                className="accent-accent"
              />
              Silenciar clip
            </label>
            {item.effects.some((e) => e.type === 'enhance_voice') && (
              <p className="text-2xs text-teal">✦ Enhance Voice ativo neste clip.</p>
            )}
          </Group>
        )}

        <button
          className="btn-danger justify-center py-2"
          onClick={() => {
            void dispatch(build.removeItems(ctx, [item.id], useEditorStore.getState().rippleEnabled))
            useEditorStore.getState().clearSelection()
          }}
        >
          Remover clip
        </button>
      </div>
    </aside>
  )
}

const FONTS = ['Inter', 'Archivo', 'Georgia', 'Impact', 'Courier New']
const CAPTION_STYLES: Array<{ id: CaptionStyle; label: string }> = [
  { id: 'sentence', label: 'Frase' },
  { id: 'word', label: 'Palavra' },
  { id: 'karaoke', label: 'Karaokê' },
  { id: 'tiktok', label: 'TikTok' },
]

/** Edição do texto na tela. Cada alteração vira um `UPDATE_TEXT`. */
function TextGroup({
  item,
  onPatch,
}: {
  item: TimelineItem
  onPatch: (patch: Parameters<typeof build.updateText>[2]) => void
}) {
  const text = item.text!
  return (
    <Group title="Texto">
      <textarea
        id="insp-text-content"
        value={text.content}
        rows={3}
        onChange={(e) => onPatch({ content: e.target.value })}
        placeholder="Digite o texto…"
        className="w-full rounded border border-ink-750 bg-ink-900 px-2 py-1.5 text-2xs
          text-ink-100 placeholder:text-ink-600 focus:border-accent focus:outline-none resize-y"
      />
      <Select
        id="insp-text-font" label="Fonte" value={text.fontFamily}
        options={FONTS.map((f) => ({ value: f, label: f }))}
        onChange={(fontFamily) => onPatch({ fontFamily })}
      />
      <Slider
        id="insp-text-size" label="Corpo" value={text.fontSize}
        min={12} max={240} step={1} format={(v) => `${Math.round(v)}px`}
        onChange={(fontSize) => onPatch({ fontSize })}
      />
      <Slider
        id="insp-text-weight" label="Peso" value={text.fontWeight}
        min={300} max={900} step={100} format={(v) => String(Math.round(v))}
        onChange={(fontWeight) => onPatch({ fontWeight })}
      />
      <ColorRow label="Cor" value={text.color} onChange={(color) => onPatch({ color })} />
      <Segmented
        label="Alinhamento"
        value={text.align}
        options={[
          { value: 'left', label: '⯇' },
          { value: 'center', label: '≡' },
          { value: 'right', label: '⯈' },
        ]}
        onChange={(align) => onPatch({ align: align as 'left' | 'center' | 'right' })}
      />
      <Slider
        id="insp-text-lh" label="Entrelinha" value={text.lineHeight}
        min={0.8} max={2.5} step={0.05} format={(v) => v.toFixed(2)}
        onChange={(lineHeight) => onPatch({ lineHeight })}
      />
      <Slider
        id="insp-text-ls" label="Espaçamento" value={text.letterSpacing}
        min={-5} max={30} step={0.5} format={(v) => `${v.toFixed(1)}px`}
        onChange={(letterSpacing) => onPatch({ letterSpacing })}
      />
      <label className="flex items-center gap-2 text-2xs text-ink-300 cursor-pointer">
        <input
          type="checkbox"
          checked={text.italic}
          onChange={(e) => onPatch({ italic: e.target.checked })}
          className="accent-accent"
        />
        Itálico
      </label>
      <label className="flex items-center gap-2 text-2xs text-ink-300 cursor-pointer">
        <input
          type="checkbox"
          checked={Boolean(text.stroke)}
          onChange={(e) =>
            onPatch({ stroke: e.target.checked ? { color: '#000000', width: 4 } : undefined })
          }
          className="accent-accent"
        />
        Contorno
      </label>
      {text.stroke && (
        <>
          <ColorRow
            label="Cor do contorno"
            value={text.stroke.color}
            onChange={(color) => onPatch({ stroke: { ...text.stroke!, color } })}
          />
          <Slider
            id="insp-text-stroke" label="Espessura" value={text.stroke.width}
            min={1} max={24} step={1} format={(v) => `${Math.round(v)}px`}
            onChange={(width) => onPatch({ stroke: { ...text.stroke!, width } })}
          />
        </>
      )}
      <label className="flex items-center gap-2 text-2xs text-ink-300 cursor-pointer">
        <input
          type="checkbox"
          checked={Boolean(text.background)}
          onChange={(e) =>
            onPatch({
              background: e.target.checked
                ? { color: '#000000', opacity: 0.6, padding: 12, radius: 8 }
                : undefined,
            })
          }
          className="accent-accent"
        />
        Caixa de fundo
      </label>
      {text.background && (
        <ColorRow
          label="Cor do fundo"
          value={text.background.color}
          onChange={(color) => onPatch({ background: { ...text.background!, color } })}
        />
      )}
    </Group>
  )
}

/** Estilo da legenda. `onPatchAll` aplica a todos os blocos de uma vez. */
function CaptionGroup({
  item,
  onPatch,
  onPatchAll,
}: {
  item: TimelineItem
  onPatch: (patch: Parameters<typeof build.updateCaptionStyle>[2]) => void
  onPatchAll: (patch: Parameters<typeof build.updateCaptionStyle>[2]) => void
}) {
  const caption = item.caption!
  return (
    <Group title="Legenda">
      <Segmented
        label="Estilo"
        value={caption.style}
        options={CAPTION_STYLES.map((s) => ({ value: s.id, label: s.label }))}
        onChange={(style) => onPatchAll({ style: style as CaptionStyle })}
      />
      <Select
        id="insp-cap-font" label="Fonte" value={caption.fontFamily}
        options={FONTS.map((f) => ({ value: f, label: f }))}
        onChange={(fontFamily) => onPatchAll({ fontFamily })}
      />
      <Slider
        id="insp-cap-size" label="Corpo" value={caption.fontSize}
        min={16} max={200} step={1} format={(v) => `${Math.round(v)}px`}
        onChange={(fontSize) => onPatchAll({ fontSize })}
      />
      <ColorRow label="Cor" value={caption.color} onChange={(color) => onPatchAll({ color })} />
      <ColorRow
        label="Palavra ativa"
        value={caption.activeColor}
        onChange={(activeColor) => onPatchAll({ activeColor })}
      />
      <ColorRow
        label="Destaque"
        value={caption.highlightColor}
        onChange={(highlightColor) => onPatchAll({ highlightColor })}
      />
      <Slider
        id="insp-cap-pos" label="Altura na tela" value={caption.positionY}
        min={0.1} max={0.95} step={0.01} format={(v) => `${Math.round(v * 100)}%`}
        onChange={(positionY) => onPatchAll({ positionY })}
      />
      <Slider
        id="insp-cap-words" label="Palavras por linha" value={caption.maxWordsPerLine}
        min={1} max={12} step={1} format={(v) => String(Math.round(v))}
        onChange={(maxWordsPerLine) => onPatchAll({ maxWordsPerLine })}
      />
      <label className="flex items-center gap-2 text-2xs text-ink-300 cursor-pointer">
        <input
          type="checkbox"
          checked={caption.uppercase}
          onChange={(e) => onPatchAll({ uppercase: e.target.checked })}
          className="accent-accent"
        />
        Caixa alta
      </label>
      <p className="text-2xs text-ink-500 leading-relaxed">
        O estilo vale para TODOS os blocos de legenda da sequência — uma legenda com
        metade dos blocos num estilo e metade noutro não é o que ninguém quer.
        <button
          className="ml-1 text-accent hover:underline"
          onClick={() => onPatch({ positionY: caption.positionY })}
          title="Reaplica a posição somente neste bloco"
        >
          só neste bloco
        </button>
      </p>
    </Group>
  )
}

function Select({
  id, label, value, options, onChange,
}: {
  id: string
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-2xs">
      <span className="text-ink-400 shrink-0">{label}</span>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 rounded border border-ink-750 bg-ink-900 px-1.5 py-1
          text-2xs text-ink-100 focus:border-accent focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}

function Segmented({
  label, value, options, onChange,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-2xs text-ink-400">{label}</span>
      <div className="flex gap-0.5 rounded bg-ink-900 p-0.5">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`flex-1 rounded py-1 text-2xs transition-colors
              ${value === o.value ? 'bg-ink-750 text-ink-100' : 'text-ink-400 hover:text-ink-200'}`}
            aria-pressed={value === o.value}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function ColorRow({
  label, value, onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-2xs">
      <span className="text-ink-400">{label}</span>
      <span className="flex items-center gap-1.5">
        <span className="font-mono text-ink-300 tnum">{value.toUpperCase()}</span>
        <input
          type="color"
          value={value.slice(0, 7)}
          onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded border border-ink-700 bg-transparent cursor-pointer p-0"
        />
      </span>
    </label>
  )
}

const CROP_LABELS: Record<string, string> = {
  top: 'Topo', right: 'Direita', bottom: 'Base', left: 'Esquerda',
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="label">{title}</h3>
      {children}
    </section>
  )
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-2xs">
      <span className="text-ink-400">{label}</span>
      <span className="font-mono text-ink-200 tnum">{value}</span>
    </div>
  )
}

function Slider({
  id, label, value, min, max, step, format, onChange,
}: {
  id: string
  label: string
  value: number
  min: number
  max: number
  step: number
  format: (value: number) => string
  onChange: (value: number) => void
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-center justify-between text-2xs">
        <span className="text-ink-400">{label}</span>
        <span className="font-mono text-ink-200 tnum">{format(value)}</span>
      </span>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="accent-accent"
      />
    </label>
  )
}
